/**
 * Reference Letters — Boris drafts the letter from the supervisor's request.
 *
 * The page opens on FINN's leavers (or, with the toggle, interim-letter requests); picking someone
 * collapses the list to one line and opens the draft below. Boris fills the People team's own
 * template (grade paragraphs selected verbatim, never written), raises what he could not decide or
 * what needs a second look, and the reviewer downloads the .docx once every point is settled. The
 * proxy holds the rules and re-checks on download (proxy-server/lib/referenceLetterDraft.js);
 * nothing about a draft is stored.
 *
 * Gated on `space.people.reference_letters.draft` (people.business-partnering@finn.com), in
 * PeopleSpace.tsx and the nav, matching the always-enforced proxy route.
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Tabs, TabsList, TabsTrigger } from '@finn-auto/design-system/atoms/tabs';
import { ChevronLeft } from 'lucide-react';
import { LeaverList } from './referenceLetters/LeaverList';
import { RequestList } from './referenceLetters/RequestList';
import { RequestLookup } from './referenceLetters/RequestLookup';
import { DraftWorkspace } from './referenceLetters/DraftWorkspace';
import { formatDate } from './referenceLetters/format';
import type { ReferenceLetterLeaver, ReferenceLetterRequest } from '../types/referenceLetterDraft';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

type ListView = 'leavers' | 'interim';
type Selection = { kind: 'leaver'; leaver: ReferenceLetterLeaver } | { kind: 'request'; request: ReferenceLetterRequest };

/**
 * The way back to the list, named for the list it restores (the active tab), not where the
 * selection came from — "find by email" opens a request from either tab. Who is open is said here
 * only when no draft opens; otherwise the draft's own header says it, once.
 */
function SelectionLine({ selection, view, withDraft, onBack }: { selection: Selection; view: ListView; withDraft: boolean; onBack: () => void }) {
  const text =
    selection.kind === 'leaver'
      ? [selection.leaver.name || 'Unnamed', selection.leaver.position, `leaves ${formatDate(selection.leaver.terminationDate)}`]
      : [selection.request.employeeName || 'Unnamed', `requested ${formatDate(selection.request.created)}`];
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button type="button" variant="outline" size="sm" className="rounded" onClick={onBack}>
        <ChevronLeft className="h-4 w-4" /> {view === 'leavers' ? 'All leavers' : 'All interim requests'}
      </Button>
      {!withDraft && <span className="text-sm text-zinc-900">{text.filter(Boolean).join(' · ')}</span>}
    </div>
  );
}

/** A leaver whose supervisor has not asked for a letter: nothing to draft from, only someone to chase. */
function NoRequest({ leaver }: { leaver: ReferenceLetterLeaver }) {
  return (
    <div className="rounded border border-solid border-pearl bg-white p-4 text-sm text-zinc-900">
      <p className="text-zinc-900">No request from {leaver.supervisorName || 'their supervisor'} yet.</p>
      <p className="mt-1">
        Boris drafts from the supervisor&apos;s tasks and grades, so there is nothing to draft until they fill in the
        reference letter form. If they used another email address, go back and find the request by that address.
      </p>
    </div>
  );
}

/** The page: the list toggle, the list (or the one line it collapses to), and the open draft. */
export default function ReferenceLettersView() {
  const [view, setView] = useState<ListView>('leavers');
  const [selection, setSelection] = useState<Selection | null>(null);
  // Bumped when a letter is sent for signature or cancelled: the hidden leaver list reloads, so its
  // badges and "Ready to draft" are right on the way back.
  const [listVersion, setListVersion] = useState(0);

  useEffect(() => {
    void logAnalyticsEvent('reference_letters_viewed');
  }, []);

  /** Opens a leaver: their request's draft, or whom to chase when there is none. */
  const selectLeaver = (leaver: ReferenceLetterLeaver) => {
    setSelection({ kind: 'leaver', leaver });
    void logAnalyticsEvent('reference_letters_leaver_selected', { hasRequest: Boolean(leaver.request) });
  };
  /** Opens a request from the interim list or the email lookup. */
  const selectRequest = (request: ReferenceLetterRequest) => setSelection({ kind: 'request', request });
  const recordId = selection?.kind === 'request' ? selection.request.id : selection?.leaver.request?.id ?? null;

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">✉️ Reference Letters</h1>
          <p className="mt-1 text-sm text-zinc-600">
            Boris drafts the letter from the supervisor&apos;s request and the People team&apos;s template. You check what
            he flags, then download the Word file.
          </p>
        </div>

        <Tabs value={view} onValueChange={(v) => { setView(v as ListView); setSelection(null); }}>
          <TabsList className="h-auto w-full justify-start gap-1 border-b border-solid border-pearl bg-transparent p-0 pb-3">
            <TabsTrigger value="leavers" className="rounded px-4 py-2">Leavers</TabsTrigger>
            <TabsTrigger value="interim" className="rounded px-4 py-2">Interim requests</TabsTrigger>
          </TabsList>
        </Tabs>

        {selection && <SelectionLine selection={selection} view={view} withDraft={Boolean(recordId)} onBack={() => setSelection(null)} />}

        {/* Hidden, not unmounted, while someone is open: the filter and the toggle survive the way back. */}
        <div data-testid="reference-letter-lists" className={selection ? 'hidden' : 'flex flex-col gap-6'}>
          {view === 'leavers' ? <LeaverList onSelect={selectLeaver} refreshKey={listVersion} /> : <RequestList onSelect={selectRequest} refreshKey={listVersion} />}
          <RequestLookup onSelect={selectRequest} />
        </div>

        {selection?.kind === 'leaver' && !recordId && <NoRequest leaver={selection.leaver} />}
        {recordId && <DraftWorkspace key={recordId} recordId={recordId} onSignatureChange={() => setListVersion((v) => v + 1)} />}
      </div>
    </div>
  );
}
